import ContentWrapper from "@/components/wrappers/ContentWrapper";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { cssVariables } from "@/utils/utils";
import { Button, Card, Tabs } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import AntIcon from "../../../common/components/icons/AntIcon";
import { DEFAULT_FILTER_LIFE_INSURANCE_TARIFF_QUERY_PARAMS } from "../api";
import { LifeInsuranceTariffFilterForm } from "../components/forms/LifeInsuranceTariffFilterForm";
import { LifeInsuranceTariffForm } from "../components/forms/LifeInsuranceTariffForm";
import { LifeInsuranceTariffGroupFilterForm } from "../components/forms/LifeInsuranceTariffGroupFilterForm";
import { LifeInsuranceTariffGroupForm } from "../components/forms/LifeInsuranceTariffGroupForm";
import { LifeInsuranceTariffGroupTable } from "../components/tables/LifeInsuranceTariffGroupTable";
import { LifeInsuranceTariffTable } from "../components/tables/LifeInsuranceTariffTable";
import { LifeInsuranceTariff, LifeInsuranceTariffFilterPageRequest, LifeInsuranceTariffGroup } from "../types";

enum TAB {
  TARIFF_GROUPS = "tariff-groups",
  TARIFFS = "tariffs",
}

export const LifeInsuranceTariffsContainer = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const tabKey = urlParams.get("tab") ?? TAB.TARIFFS;

  const [tariffGroupFormOpen, setTariffGroupFormOpen] = useState(false);
  const [tariffGroupToUpdate, setTariffGroupToUpdate] = useState<LifeInsuranceTariffGroup>();

  const [tariffFormOpen, setTariffFormOpen] = useState(false);
  const [tariffToUpdate, setTariffToUpdate] = useState<LifeInsuranceTariff>();

  const searchParams = useFilterSearchParams<LifeInsuranceTariffFilterPageRequest>({
    params: ["groupIds"],
    defaultQueryParamsObject: DEFAULT_FILTER_LIFE_INSURANCE_TARIFF_QUERY_PARAMS,
  });

  const moveToTab = (tabKey: string) => {
    const tab = tabKey === TAB.TARIFFS ? undefined : tabKey;

    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: { pageIndex: undefined, keyword: undefined, groupIds: undefined, tab },
          append: true,
        }),
      },
      {
        replace: true,
      },
    );
  };

  const handleTariffGroupCreateClick = () => {
    setTariffGroupFormOpen(true);
  };

  const handleTariffGroupUpdateClick = (group: LifeInsuranceTariffGroup) => {
    setTariffGroupFormOpen(true);
    setTariffGroupToUpdate(group);
  };

  const handleTariffGroupFormCancel = () => {
    setTariffGroupFormOpen(false);
    setTariffGroupToUpdate(undefined);
  };

  const handleTariffCreateClick = () => {
    setTariffFormOpen(true);
  };

  const handleTariffUpdateClick = (tariff: LifeInsuranceTariff) => {
    setTariffFormOpen(true);
    setTariffToUpdate(tariff);
  };

  const handleTariffFormCancel = () => {
    setTariffFormOpen(false);
    setTariffToUpdate(undefined);
  };

  return (
    <ContentWrapper>
      <Tabs
        tabBarStyle={{
          backgroundColor: "white",
          padding: "0 24px",
          marginBottom: 24,
          borderRadius: 8,
          boxShadow: `0 1px 3px 0 ${cssVariables.colorShadow}, 0 1px 2px 0 ${cssVariables.colorShadow}`,
          border: `1px solid ${cssVariables.colorHover}`,
          overflow: "hidden",
        }}
        activeKey={tabKey}
        onChange={moveToTab}
        items={[
          {
            key: TAB.TARIFF_GROUPS,
            icon: <AntIcon type="group" />,
            label: t("lifeInsuranceTariff.titles.tariffGroups"),
            children: (
              <div>
                <Card
                  className="card-box"
                  title={<h2>{t("lifeInsuranceTariff.titles.tariffGroupsAdmin")}</h2>}
                  extra={
                    <Button type="primary" icon={<AntIcon type="plus" />} onClick={handleTariffGroupCreateClick}>
                      {t("lifeInsuranceTariff.actions.createTariffGroup")}
                    </Button>
                  }
                >
                  <LifeInsuranceTariffGroupFilterForm searchParams={searchParams} />
                </Card>

                <Card className="card-box">
                  <LifeInsuranceTariffGroupTable
                    searchParams={searchParams}
                    onUpdateClick={handleTariffGroupUpdateClick}
                  />
                </Card>

                <LifeInsuranceTariffGroupForm
                  open={tariffGroupFormOpen}
                  tariffGroup={tariffGroupToUpdate}
                  onFormCancel={handleTariffGroupFormCancel}
                />
              </div>
            ),
          },
          {
            key: TAB.TARIFFS,
            icon: <AntIcon type="unordered-list" />,
            label: t("lifeInsuranceTariff.titles.tariffs"),
            children: (
              <div>
                <Card
                  className="card-box"
                  title={<h2>{t("lifeInsuranceTariff.titles.tariffsAdmin")}</h2>}
                  extra={
                    <Button type="primary" icon={<AntIcon type="plus" />} onClick={handleTariffCreateClick}>
                      {t("lifeInsuranceTariff.actions.createTariff")}
                    </Button>
                  }
                >
                  <LifeInsuranceTariffFilterForm searchParams={searchParams} />
                </Card>

                <Card className="card-box">
                  <LifeInsuranceTariffTable searchParams={searchParams} onUpdateClick={handleTariffUpdateClick} />
                </Card>

                <LifeInsuranceTariffForm
                  open={tariffFormOpen}
                  tariff={tariffToUpdate}
                  onFormCancel={handleTariffFormCancel}
                />
              </div>
            ),
          },
        ]}
      />
    </ContentWrapper>
  );
};
